import React, { useState, useRef, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Form } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiDrag,
  mdiChevronDown,
  mdiChevronRight,
  mdiPlus,
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiCheck,
} from "@mdi/js";
import "./editableSectionCard.css";

const EditableSectionCard = ({
  section,
  onSectionUpdate,
  onSectionDelete,
  onSubsectionUpdate,
  onSubsectionDelete,
  onAddSubsection,
  allSections = [],
  allSubsections = [],
  isDragDisabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section?.id,
    disabled: isDragDisabled,
  });

  const [isEditingName, setIsEditingName] = useState(
    section?.isEditing || false
  );
  const [tempName, setTempName] = useState(section?.name || "");
  const [isCollapsed, setIsCollapsed] = useState(section?.collapsed || false);
  const [validationError, setValidationError] = useState("");
  const nameInputRef = useRef(null);

  // Sync internal collapse state with section prop
  useEffect(() => {
    setIsCollapsed(section?.collapsed || false);
  }, [section?.collapsed]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Completely hide original when dragging
  };

  const isOtherSection = section?.name?.toLowerCase().trim() === "other";
  const isNewSection = section?.name === "" && section?.isEditing === true;

  const validateSectionName = (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return "Section name cannot be empty";
    }

    // Check for duplicates (excluding current section)
    const isDuplicate = allSections.some(
      (s) =>
        s.id !== section?.id &&
        s.name.toLowerCase().trim() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      return `Name "${trimmedName}" already exists.`;
    }

    return "";
  };

  useEffect(() => {
    if (isEditingName && !isOtherSection && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName, isOtherSection]);

  const handleStartEdit = () => {
    if (!isOtherSection) {
      setTempName(section?.name || "");
      setIsEditingName(true);
    }
  };

  const handleCancelEdit = () => {
    setTempName(section?.name || "");
    setValidationError("");
    setIsEditingName(false);
  };

  const handleBlur = () => {
    // For new sections, only remove if empty (not if just invalid)
    if (isNewSection) {
      if (!tempName.trim()) {
        onSectionDelete(section?.id);
        return;
      }
      // For new sections with content, show validation errors instead of deleting
      const error = validateSectionName(tempName);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    // For existing sections, check for validation errors
    const error = validateSectionName(tempName);
    if (error) {
      // Show validation error and stay in edit mode
      setValidationError(error);
      return;
    }

    // Only save if there are no validation errors
    handleSaveEdit();
  };

  const handleSaveEdit = () => {
    const error = validateSectionName(tempName);
    if (error) {
      setValidationError(error);
      return;
    }

    const trimmedName = tempName.trim();
    setValidationError("");
    onSectionUpdate(section?.id, { name: trimmedName, isEditing: false });
    setIsEditingName(false);
  };

  const handleInputChange = (e) => {
    setTempName(e.target.value);
    setValidationError(""); // Clear validation error when user types
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      if (isNewSection) {
        onSectionDelete(section?.id);
      } else {
        handleCancelEdit();
      }
    }
  };

  const handleDelete = () => {
    if (!isOtherSection) {
      onSectionDelete(section?.id);
    }
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleAddSubsection = () => {
    onAddSubsection(section?.id);
  };

  const hasSubsections = section?.subsections && section.subsections.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`editable-section-card section-card-base ${
        isDragging ? "dragging" : ""
      }`}
    >
      <div className="editable-section-header section-header-base">
        {!isDragDisabled && (
          <div className="drag-handle" {...listeners}>
            <Icon path={mdiDrag} size={1} className="text-muted" />
          </div>
        )}

        {hasSubsections && (
          <button
            className="collapse-toggle clickable"
            onClick={handleToggleCollapse}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <Icon path={mdiChevronRight} size={1} />
            ) : (
              <Icon path={mdiChevronDown} size={1} />
            )}
          </button>
        )}

        {isEditingName && !isOtherSection ? (
          <div className="section-name-container">
            <div className="section-name-edit">
              <div className="input-container">
                <Form.Control
                  ref={nameInputRef}
                  type="text"
                  value={tempName}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  placeholder="Section Name..."
                  size="sm"
                  className={validationError ? "is-invalid" : ""}
                />
                {validationError && (
                  <div className="invalid-feedback d-block mt-1">
                    {validationError}
                  </div>
                )}
              </div>
              <div className="edit-actions">
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={!tempName.trim()}
                  className="p-0 text-success"
                >
                  <Icon
                    path={mdiCheck}
                    size={0.875}
                    style={{ color: "#6C757D" }}
                  />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="section-title">
            <span className="section-name">
              {section?.name || "Section Name..."}
            </span>
            <div className="section-actions">
              <Button
                variant="link"
                size="sm"
                onClick={handleAddSubsection}
                className="p-0 text-success"
                title="Add Subsection"
              >
                <Icon
                  path={mdiPlus}
                  size={0.875}
                  style={{ color: "#6C757D" }}
                />
              </Button>
              {!isOtherSection && (
                <>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleStartEdit}
                    className="p-0 text-muted"
                    title="Edit Section"
                  >
                    <Icon
                      path={mdiPencilOutline}
                      size={0.875}
                      style={{ color: "#6C757D" }}
                    />
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleDelete}
                    className="p-0 text-danger"
                    title="Delete Section"
                  >
                    <Icon path={mdiTrashCanOutline} size={0.875} />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {hasSubsections && !isCollapsed && (
        <div className="subsections-container">
          {section?.subsections?.map((subsection) => (
            <EditableSubsectionItem
              key={subsection.id}
              subsection={subsection}
              sectionId={section?.id}
              onSubsectionUpdate={onSubsectionUpdate}
              onSubsectionDelete={onSubsectionDelete}
              allSubsections={allSubsections}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EditableSubsectionItem = ({
  subsection,
  sectionId,
  onSubsectionUpdate,
  onSubsectionDelete,
  allSubsections = [],
}) => {
  const [isEditing, setIsEditing] = useState(subsection?.isEditing || false);
  const [tempName, setTempName] = useState(subsection?.name || "");
  const [validationError, setValidationError] = useState("");
  const nameInputRef = useRef(null);

  const isOtherSubsection = subsection?.name?.toLowerCase().trim() === "other";
  const isNewSubsection =
    subsection?.name === "" && subsection?.isEditing === true;

  const validateSubsectionName = (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return "Subsection name cannot be empty";
    }

    // Check for duplicates within the same section (excluding current subsection)
    const isDuplicate = allSubsections.some(
      (s) =>
        s.id !== subsection?.id &&
        s.name.toLowerCase().trim() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      return `Name "${trimmedName}" already exists.`;
    }

    return "";
  };

  useEffect(() => {
    if (isEditing && !isOtherSubsection && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditing, isOtherSubsection]);

  const handleSubsectionStartEdit = () => {
    if (!isOtherSubsection) {
      setTempName(subsection?.name || "");
      setIsEditing(true);
    }
  };

  const handleSubsectionBlur = () => {
    // For new subsections, only remove if empty (not if just invalid)
    if (isNewSubsection) {
      if (!tempName.trim()) {
        onSubsectionDelete(sectionId, subsection?.id);
        return;
      }
      // For new subsections with content, show validation errors instead of deleting
      const error = validateSubsectionName(tempName);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    // For existing subsections, check for validation errors
    const error = validateSubsectionName(tempName);
    if (error) {
      // Show validation error and stay in edit mode
      setValidationError(error);
      return;
    }

    // Only save if there are no validation errors
    handleSubsectionSaveEdit();
  };

  const handleSubsectionSaveEdit = () => {
    const error = validateSubsectionName(tempName);
    if (error) {
      setValidationError(error);
      return;
    }

    const trimmedName = tempName.trim();
    setValidationError("");
    onSubsectionUpdate(sectionId, subsection?.id, {
      name: trimmedName,
      isEditing: false,
    });
    setIsEditing(false);
  };

  const handleSubsectionInputChange = (e) => {
    setTempName(e.target.value);
    setValidationError(""); // Clear validation error when user types
  };

  const handleSubsectionKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubsectionSaveEdit();
    } else if (e.key === "Escape") {
      if (isNewSubsection) {
        onSubsectionDelete(sectionId, subsection?.id);
      } else {
        // For existing subsections, just save the changes since there's no cancel
        handleSubsectionSaveEdit();
      }
    }
  };

  const handleSubsectionDelete = () => {
    if (!isOtherSubsection) {
      onSubsectionDelete(sectionId, subsection?.id);
    }
  };

  return (
    <div className="editable-subsection-item subsection-item-base">
      <div className="subsection-content">
        {isEditing && !isOtherSubsection ? (
          <div className="subsection-name-edit">
            <div className="input-container">
              <Form.Control
                ref={nameInputRef}
                type="text"
                value={tempName}
                onChange={handleSubsectionInputChange}
                onKeyDown={handleSubsectionKeyDown}
                onBlur={handleSubsectionBlur}
                placeholder="Subsection Name..."
                size="sm"
                className={validationError ? "is-invalid" : ""}
              />
              {validationError && (
                <div className="invalid-feedback d-block mt-1">
                  {validationError}
                </div>
              )}
            </div>
            <div className="edit-actions">
              <Button
                variant="link"
                size="sm"
                onClick={handleSubsectionSaveEdit}
                disabled={!tempName.trim()}
                className="p-0 text-success"
              >
                <Icon
                  path={mdiCheck}
                  size={0.875}
                  style={{ color: "#6C757D" }}
                />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <span className="subsection-name">
              {subsection?.name || "Subsection Name..."}
            </span>
            {!isOtherSubsection && (
              <div className="subsection-actions">
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSubsectionStartEdit}
                  className="p-0 text-muted"
                  title="Edit Subsection"
                >
                  <Icon
                    path={mdiPencilOutline}
                    size={0.875}
                    style={{ color: "#6C757D" }}
                  />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSubsectionDelete}
                  className="p-0 text-danger"
                  title="Delete Subsection"
                >
                  <Icon path={mdiTrashCanOutline} size={0.875} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditableSectionCard;
